import { useState, useRef, useEffect, useMemo, forwardRef } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { allProducts, type Product, type ProductCategory } from "@/data/productDatabase";
import { compareCategoryLabels } from "@/data/compareCategories";
import VerificationBadge from "@/components/products/VerificationBadge";

export type PriceTier = "all" | "under100" | "100to300" | "over300";

interface ProductSearchComboboxProps {
  selectedId: string;
  onSelect: (id: string) => void;
  excludeId: string;
  label: string;
  categoryFilter?: ProductCategory;
  priceFilter?: PriceTier;
  /** Optional whitelist — when set, only these product ids are shown. */
  allowedIds?: Set<string>;
}

const matchesPriceTier = (price: number, tier: PriceTier): boolean => {
  switch (tier) {
    case "under100": return price < 100;
    case "100to300": return price >= 100 && price <= 300;
    case "over300": return price > 300;
    default: return true;
  }
};

const ProductSearchCombobox = forwardRef<HTMLDivElement, ProductSearchComboboxProps>(({ selectedId, onSelect, excludeId, label, categoryFilter, priceFilter = "all", allowedIds }, ref) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const baseProducts = useMemo(
    () => {
      let items = categoryFilter ? allProducts.filter((p) => p.category === categoryFilter) : allProducts;
      if (priceFilter !== "all") items = items.filter((p) => matchesPriceTier(p.price, priceFilter));
      if (allowedIds) items = items.filter((p) => allowedIds.has(p.id));
      return items;
    },
    [categoryFilter, priceFilter, allowedIds]
  );

  // Use full product list for selected lookup so the chosen product still shows even if filtered out
  const selected = allProducts.find((p) => p.id === selectedId);

  const available = useMemo(
    () => baseProducts.filter((p) => p.id !== excludeId),
    [baseProducts, excludeId]
  );

  const filtered = useMemo(() => {
    let results = available;
    if (search.trim()) {
      const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
      results = available.filter((p) => {
        const haystack = `${p.brand} ${p.model}`.toLowerCase();
        return terms.every((term) => haystack.includes(term));
      });
    }
    // Always sort by brand then model for consistent grouping
    return [...results].sort((a, b) =>
      a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model)
    );
  }, [available, search]);

  // Group by brand for flat/category mode, by category for all-products mode
  const grouped = useMemo(() => {
    if (categoryFilter) {
      // Group by brand within the category
      const groups: Record<string, Product[]> = {};
      for (const p of filtered) {
        if (!groups[p.brand]) groups[p.brand] = [];
        groups[p.brand].push(p);
      }
      return groups;
    }
    const groups: Record<string, Product[]> = {};
    for (const p of filtered) {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    }
    return groups;
  }, [filtered, categoryFilter]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const renderItem = (p: Product, index: number) => (
    <button
      key={`${p.id}-${p.brand}-${p.model}-${index}`}
      type="button"
      onClick={() => {
        onSelect(p.id);
        setOpen(false);
        setSearch("");
      }}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-base font-mono transition-colors hover:bg-accent/10 ${
        p.id === selectedId
          ? "bg-primary/10 text-primary"
          : "text-foreground"
      }`}
    >
      <span className="truncate">
        <span className="font-bold">{p.brand}</span>{" "}
        <span className="text-muted-foreground">{p.model}</span>
      </span>
      <span className="flex items-center gap-2 shrink-0">
        {(p.category === "amplifier" || p.category === "speaker") && (
          <VerificationBadge product={p} />
        )}
        <span className="text-sm text-muted-foreground">${p.price}</span>
      </span>
    </button>
  );

  return (
    <div
      className="space-y-1.5"
      ref={(node) => {
        containerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
    >
      <label className="text-sm font-mono font-semibold text-muted-foreground uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setOpen(!open);
            if (open) setSearch("");
          }}
          className="w-full flex items-center gap-2 bg-surface-sunken border border-border rounded-lg px-3 py-3 text-left text-base font-mono text-foreground cursor-pointer hover:border-primary/50 focus:border-primary/50 focus:outline-none transition-colors"
        >
          <span className="flex-1 truncate">
            {selected ? `${selected.brand} ${selected.model}` : "Select a product..."}
          </span>
          {selected && (
            <span className="text-muted-foreground text-sm">${selected.price}</span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-xl overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border bg-surface-sunken/50">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to search brand or model..."
                className="flex-1 bg-transparent text-base font-mono text-foreground placeholder:text-muted-foreground outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto overscroll-contain">
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-base text-muted-foreground font-mono">
                  No products found
                </div>
              ) : (
                Object.entries(grouped!).map(([groupLabel, products]) => (
                  <div key={groupLabel}>
                    <div className="px-3 py-1.5 text-xs font-mono text-primary uppercase tracking-wider font-bold bg-surface-sunken/30 sticky top-0">
                      {categoryFilter
                        ? groupLabel
                        : (compareCategoryLabels[groupLabel as ProductCategory] ?? groupLabel)}{" "}
                      ({products.length})
                    </div>
                    {products.map((product, index) => renderItem(product, index))}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ProductSearchCombobox.displayName = "ProductSearchCombobox";

export default ProductSearchCombobox;
